
  import { defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter, useRoute } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { PropertyTypeObject } from '@/store/modules/Auctions/PropertyTypesModule';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';

  export default defineComponent({
    name: 'auctions-property-types-editing',
    components: {
      ErrorMessage,
      Field,
      Form,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const { can } = useAbility();
      const thumbnail = ref('/media/avatars/blank-image.jpg');
      const submitButton = ref<HTMLButtonElement | null>(null);
      const iconFile = ref<File | null>(null);

      const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
          iconFile.value = file;
          thumbnail.value = URL.createObjectURL(file);
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('propertyTypesUpdating'), [
          translate('auctions'),
        ]);
      });

      const { data } = await store.dispatch(
        Actions.GET_AUCTION_PROPERTY_TYPE,
        route.params.id
      );

      const type = ref<PropertyTypeObject>(data.data);

      type.value = data.data;
      thumbnail.value = type.value.icon
        ? type.value.icon
        : '/media/avatars/blank-image.jpg';

      const translate = (text: string) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const previewImage = (e) => {
        //need to enhance this
        const file = e.target.files[0];
        thumbnail.value = URL.createObjectURL(file);
        iconFile.value = file;
      };

      const validationSchema = Yup.object().shape({
        titleEn: Yup.string()
          .required(() => translate('EN_NAME_IS_REQUIRED_FIELD'))
          .label('English Name'),
        titleAr: Yup.string()
          .required(() => translate('AR_NAME_IS_REQUIRED_FIELD'))
          .label('Arabic Name'),
      });

      const onSubmitUpdate = async (values) => {
        if (submitButton.value) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        const { images, ...value } = values;
        const updatedType = await store.dispatch(
          Actions.UPDATE_AUCTION_PROPERTY_TYPE,
          {
            id: type.value?.id,
            data: value,
          }
        );

        if (images != null) {
          if (iconFile.value) {
            const formData = new FormData();
            formData.append('images', iconFile.value);

            await store.dispatch(Actions.UPLOAD_PROPERTY_TYPE_ICON, {
              data: formData,
              id: updatedType.data.id,
            });
          }
        }
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_PROPERTY_TYPE'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'auction-property-types-listing' });
          });
        }
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        onSubmitUpdate,
        submitButton,
        translate,
        validationSchema,
        goBack,
        type,
        can,
        thumbnail,
        handleFileUpload,
        iconFile,
      };
    },
  });
